import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Building2, Users, FileText, CheckCircle2, ShieldCheck, Loader2, Plus, Trash2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";

export default function Organizations() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/organizations");
      setOrgs(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-8" data-testid="organizations-root">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#7A786F]">
            <ShieldCheck className="h-3.5 w-3.5 text-[#D85A30]" /> Superadmin
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#2C2C2A] mt-1">Organizaciones</h1>
          <p className="text-[#7A786F] mt-1">Todas las cuentas de Eksvidi y su uso de la plataforma.</p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button className="btn-eks-primary rounded-[12px]" data-testid="create-org-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Crear organización
            </Button>
          </DialogTrigger>
          <CreateOrgDialog onCreated={() => { load(); setOpenCreate(false); }} />
        </Dialog>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-10 text-center text-[#7A786F]">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      ) : orgs.length === 0 ? (
        <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-10 text-center text-[#7A786F]">
          Aún no hay organizaciones registradas.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orgs.map((o) => {
            const completion = o.siniestros_total
              ? Math.round((o.siniestros_finalized / o.siniestros_total) * 100)
              : 0;
            const isOwn = o.id === user?.org_id;
            return (
              <div key={o.id} className="bg-white border border-[#E5E2D8] rounded-[14px] p-5 flex flex-col" data-testid={`org-${o.slug}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-[10px] bg-[#FAECE7] flex items-center justify-center shrink-0">
                    <Building2 className="h-5 w-5 text-[#D85A30]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-lg font-semibold text-[#2C2C2A] truncate">{o.name}</div>
                    <div className="text-xs text-[#7A786F] truncate">/{o.slug}</div>
                  </div>
                  {isOwn && (
                    <span className="text-[10px] uppercase tracking-wider text-[#1D9E75] bg-[#E6F5EF] rounded-full px-2 py-0.5 font-semibold">Tuya</span>
                  )}
                </div>

                {o.founder && (
                  <div className="mt-4 text-xs">
                    <div className="text-[#7A786F] uppercase tracking-wider text-[10px] font-semibold mb-1">Fundador</div>
                    <div className="text-[#2C2C2A]">{o.founder.name || o.founder.email}</div>
                    <div className="text-[#7A786F] truncate">{o.founder.email}</div>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <Stat icon={Users}      label="Miembros"   value={o.members_count} />
                  <Stat icon={FileText}   label="Expedientes" value={o.siniestros_total} />
                  <Stat icon={CheckCircle2} label="Cerrados"  value={o.siniestros_finalized} />
                </div>

                <div className="mt-4">
                  <div className="text-[11px] text-[#7A786F] mb-1.5 flex items-center justify-between">
                    <span>Finalización</span>
                    <span className="font-medium text-[#2C2C2A]">{completion}%</span>
                  </div>
                  <div className="h-1.5 bg-[#FBFAF6] rounded-full overflow-hidden">
                    <div className="h-full bg-[#D85A30]" style={{ width: `${completion}%` }} />
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-[#E5E2D8] flex items-center justify-between text-[10px] text-[#7A786F]">
                  <span>Alta: {o.created_at ? new Date(o.created_at).toLocaleDateString("es-ES") : "—"}</span>
                  <div className="flex items-center gap-3">
                    <a
                      href={`/equipo?org=${o.id}`}
                      className="text-[#D85A30] hover:underline inline-flex items-center gap-1 font-medium uppercase tracking-wider"
                      data-testid={`open-org-${o.slug}`}
                    >
                      Abrir equipo
                    </a>
                    {!isOwn && (
                      <button
                        onClick={() => setDeleteTarget(o)}
                        className="text-[#A32D2D] hover:text-[#7A1F1F] inline-flex items-center gap-1 font-medium uppercase tracking-wider"
                        data-testid={`delete-org-${o.slug}`}
                      >
                        <Trash2 className="h-3 w-3" /> Eliminar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DeleteOrgDialog
          org={deleteTarget}
          onConfirmed={() => { load(); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      </Dialog>
    </div>
  );
}

function CreateOrgDialog({ onCreated }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await api.post("/admin/organizations", { name: name.trim() });
      toast.success("Organización creada");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear");
    } finally { setLoading(false); }
  };
  return (
    <DialogContent data-testid="create-org-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading">Crear nueva organización</DialogTitle>
        <DialogDescription>
          Una organización es un gabinete o aseguradora. Sus peritos solo verán los expedientes de su propia organización.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="org-name">Nombre *</Label>
          <Input id="org-name" required maxLength={80}
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Ej: Aseguradora ACME"
            className="rounded-[12px] border-[#E5E2D8]"
            data-testid="org-name-input"
          />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={loading || !name.trim()} className="btn-eks-primary rounded-[12px]" data-testid="org-create-submit">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Crear
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function DeleteOrgDialog({ org, onConfirmed, onCancel }) {
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  if (!org) return null;
  const required = org.name;
  const matches = confirmText.trim() === required;
  const submit = async (e) => {
    e.preventDefault();
    if (!matches) return;
    setLoading(true);
    try {
      const r = await api.delete(`/admin/organizations/${org.id}`);
      toast.success(`Organización eliminada · ${r.data.deleted.members} miembros, ${r.data.deleted.siniestros} expedientes`);
      onConfirmed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    } finally { setLoading(false); }
  };
  return (
    <DialogContent data-testid="delete-org-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading text-[#A32D2D] flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" /> Eliminar organización
        </DialogTitle>
        <DialogDescription className="text-[#2C2C2A]">
          Vas a eliminar <b>{org.name}</b> y de forma <b>permanente e irreversible</b>:
        </DialogDescription>
      </DialogHeader>
      <ul className="text-sm text-[#2C2C2A] bg-[#FAECE7] border border-[#E5E2D8] rounded-[12px] p-4 space-y-1">
        <li><b>{org.members_count}</b> miembros perderán acceso</li>
        <li><b>{org.siniestros_total}</b> expedientes ({org.siniestros_finalized} finalizados) y sus fotos, chat y transcripciones</li>
        <li>Todas las invitaciones pendientes</li>
      </ul>
      <form onSubmit={submit} className="space-y-3 mt-2">
        <Label htmlFor="confirm">
          Para confirmar, escribe el nombre <span className="font-mono text-[#A32D2D]">{required}</span>:
        </Label>
        <Input id="confirm" required
          value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
          autoFocus
          className="rounded-[12px] border-[#E5E2D8]"
          data-testid="delete-org-confirm-input"
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} className="rounded-[12px] border-[#E5E2D8]" data-testid="delete-org-cancel">
            Cancelar
          </Button>
          <Button type="submit" disabled={!matches || loading}
            className="rounded-[12px] bg-[#A32D2D] hover:bg-[#7A1F1F] text-white"
            data-testid="delete-org-confirm">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Eliminar definitivamente
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] py-2.5 px-2">
      <Icon className="h-3.5 w-3.5 text-[#D85A30] mx-auto mb-0.5" />
      <div className="font-heading font-bold text-[#2C2C2A]">{value}</div>
      <div className="text-[10px] text-[#7A786F] uppercase tracking-wider">{label}</div>
    </div>
  );
}
